import { useMemo } from 'react';
import { CallIntelligenceWetags } from '@wetags/call-intelligence';
import { useAppScopeStore } from '@frontend/scope';
import { CallIntelSubscriptionPlan } from '../constants';
import { CallIntelSubscriptionPlanValue } from '../types';
import { useIsCallIntelEligibleAccount } from './use-is-call-intel-eligible-account';

type UseCallIntelSubscriptionParams = {
  includeAiReceptionist?: boolean;
  locationIds?: string[];
};

type UseCallIntelSubscription = {
  currentPlan: CallIntelSubscriptionPlanValue;
  locationPlanMap: Record<string, CallIntelSubscriptionPlanValue>;
  callIntelBasePlanLocationIds: string[];
  callIntelProPlanLocationIds: string[];
  isCallIntelBaseActive: boolean;
  isCallIntelProActive: boolean;
  isCallIntelUpgradeEligible: boolean;
  isCallIntelFreeTrialEligible: boolean;
  isCallIntelActive: boolean;
  isLoading: boolean;
};

const getHighestPlan = (plans: CallIntelSubscriptionPlanValue[]): CallIntelSubscriptionPlanValue => {
  if (plans.length === 0) {
    return CallIntelSubscriptionPlan.NONE;
  }

  return plans.reduce<CallIntelSubscriptionPlanValue>((highestPlan, plan) => {
    return plan > highestPlan ? plan : highestPlan;
  }, CallIntelSubscriptionPlan.NONE);
};

export const useCallIntelSubscription = ({
  includeAiReceptionist = true,
  locationIds,
}: UseCallIntelSubscriptionParams = {}): UseCallIntelSubscription => {
  const { selectedLocationIds: appScopeLocationIds } = useAppScopeStore();
  const selectedLocationIds = locationIds || appScopeLocationIds;

  const {
    activeLocationIds,
    isCallIntelUpgradeEligible,
    isCallIntelFreeTrialEligible,
    isLoading: isEligibilityLoading,
  } = useIsCallIntelEligibleAccount({
    includeAiReceptionist,
    locationIds: selectedLocationIds,
  });

  const callIntelProPlanLocationIds = useMemo(() => [...activeLocationIds], [activeLocationIds]);

  const locationsAlreadyAssignedHigherPlan = useMemo(() => {
    return new Set(callIntelProPlanLocationIds);
  }, [callIntelProPlanLocationIds]);

  const locationsNeedingPlanLookup = useMemo(() => {
    return (selectedLocationIds || []).filter((locationId) => !locationsAlreadyAssignedHigherPlan.has(locationId));
  }, [locationsAlreadyAssignedHigherPlan, selectedLocationIds]);

  // Fetch base plan status for locations that don't already have pro plan
  const { dataByEntityId, isLoading: isBasePlanLoading } = CallIntelligenceWetags.useWetagQueries(
    'call-intel-base-plan',
    locationsNeedingPlanLookup
  );

  const callIntelBasePlanLocationIds = useMemo(() => {
    return Object.entries(dataByEntityId || {})
      .filter(([, hasBasePlan]) => hasBasePlan)
      .map(([locationId]) => locationId);
  }, [dataByEntityId]);

  const locationPlanMap = useMemo(() => {
    const proPlanLocations = new Set(callIntelProPlanLocationIds);
    const basePlanLocations = new Set(callIntelBasePlanLocationIds);

    return (selectedLocationIds || []).reduce<Record<string, CallIntelSubscriptionPlanValue>>((acc, locationId) => {
      if (proPlanLocations.has(locationId)) {
        acc[locationId] = CallIntelSubscriptionPlan.PRO;
      } else if (basePlanLocations.has(locationId)) {
        acc[locationId] = CallIntelSubscriptionPlan.BASE;
      } else {
        acc[locationId] = CallIntelSubscriptionPlan.NONE;
      }

      return acc;
    }, {});
  }, [callIntelBasePlanLocationIds, callIntelProPlanLocationIds, selectedLocationIds]);

  const currentPlan = useMemo(() => {
    return getHighestPlan(Object.values(locationPlanMap));
  }, [locationPlanMap]);

  const isCallIntelProActive = callIntelProPlanLocationIds.length > 0;
  const isCallIntelBaseActive = callIntelBasePlanLocationIds.length > 0;
  const isCallIntelActive = isCallIntelProActive || isCallIntelBaseActive;

  return {
    currentPlan,
    locationPlanMap,
    callIntelBasePlanLocationIds,
    callIntelProPlanLocationIds,
    isCallIntelBaseActive,
    isCallIntelProActive,
    isCallIntelUpgradeEligible,
    isCallIntelFreeTrialEligible,
    isCallIntelActive,
    isLoading: isEligibilityLoading || isBasePlanLoading,
  };
};
